import { type ReactNode, useRef } from "react";
import { Button, Heading, Text, View, Flex } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { isFeatureEnabled } from "../../utils/featureFlags";
import FeatureShowcase from "../../components/marketing/FeatureShowcase";
import HeroSection from "../../components/marketing/HeroSection";
import DemoModal from "../../components/marketing/DemoModal";
import HowItWorksSection from "../../components/marketing/HowItWorksSection";
import AIIntegrationSection from "../../components/marketing/AIIntegrationSection";
import type { HowItWorksStep } from "../../types/how-it-works";
import type { AIUseCase } from "../../types/ai-integration";
import { useState } from "react";
import "./LandingPageModern.css";

const AnimatedSection = ({
  children,
  delay = 0,
}: {
  children: ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ duration: 0.6, delay }}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 50 },
      }}
    >
      {children}
    </motion.div>
  );
};

const howItWorksSteps: HowItWorksStep[] = [
  {
    stepNumber: 1,
    titleKey: "landing.howItWorks.step1.title",
    descriptionKey: "landing.howItWorks.step1.description",
    icon: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
  },
  {
    stepNumber: 2,
    titleKey: "landing.howItWorks.step2.title",
    descriptionKey: "landing.howItWorks.step2.description",
    icon: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
  },
  {
    stepNumber: 3,
    titleKey: "landing.howItWorks.step3.title",
    descriptionKey: "landing.howItWorks.step3.description",
    icon: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
];

const aiUseCases: AIUseCase[] = [
  {
    titleKey: "landing.aiIntegration.useCases.mcp.title",
    descriptionKey: "landing.aiIntegration.useCases.mcp.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
    accentColor: "#3b82f6",
  },
  {
    titleKey: "landing.aiIntegration.useCases.aiAnalysis.title",
    descriptionKey: "landing.aiIntegration.useCases.aiAnalysis.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    accentColor: "#8b5cf6",
  },
  {
    titleKey: "landing.aiIntegration.useCases.careerPlanning.title",
    descriptionKey: "landing.aiIntegration.useCases.careerPlanning.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    accentColor: "#f59e0b",
  },
  {
    titleKey: "landing.aiIntegration.useCases.suggestions.title",
    descriptionKey: "landing.aiIntegration.useCases.suggestions.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
    accentColor: "#10b981",
  },
  {
    titleKey: "landing.aiIntegration.useCases.githubSync.title",
    descriptionKey: "landing.aiIntegration.useCases.githubSync.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" />
      </svg>
    ),
    accentColor: "#06b6d4",
  },
  {
    titleKey: "landing.aiIntegration.useCases.teamAI.title",
    descriptionKey: "landing.aiIntegration.useCases.teamAI.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    accentColor: "#ec4899",
  },
  {
    titleKey: "landing.aiIntegration.useCases.inbox.title",
    descriptionKey: "landing.aiIntegration.useCases.inbox.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12h-6l-2 3h-4l-2-3H2" />
        <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
      </svg>
    ),
    accentColor: "#8b5cf6",
  },
  {
    titleKey: "landing.aiIntegration.useCases.trust.title",
    descriptionKey: "landing.aiIntegration.useCases.trust.description",
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    accentColor: "#f59e0b",
  },
];

function LandingPageModern() {
  const navigate = useNavigate();
  const { t } = useTranslation("marketing");
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const isLive = isFeatureEnabled("LANDING_PAGE_LIVE");

  // Check if running on localhost to prevent Storylane stats pollution
  // Only load Storylane demo on production to prevent stats pollution.
  // Production hostnames: www.skillbubble.com, app.skillbubble.com, skillbubble.com
  const isProd =
    window.location.hostname === "www.skillbubble.com" ||
    window.location.hostname === "app.skillbubble.com" ||
    window.location.hostname === "skillbubble.com";
  const isLocalhost = !isProd;

  return (
    <View>
      {/* Hero Section */}
      <HeroSection
        isLive={isLive}
        onSignUp={() => navigate("/signup")}
        onOpenDemo={() => setIsDemoModalOpen(true)}
        isLocalhost={isLocalhost}
      />

      {/* Feature Showcase — live component previews */}
      <FeatureShowcase />

      {/* How It Works Section */}
      <HowItWorksSection steps={howItWorksSteps} />

      {/* Abstract Value Proposition Section */}
      <View className="abstract-value-section">
        <div className="abstract-background" aria-hidden="true">
          <div className="abstract-shape shape-1"></div>
          <div className="abstract-shape shape-2"></div>
          <div className="abstract-shape shape-3"></div>
        </div>

        <div className="value-content">
          <AnimatedSection>
            <Heading
              level={2}
              textAlign="center"
              marginBottom="1rem"
              className="section-title-modern"
            >
              Your Skills. Your Story. Your Future.
            </Heading>
            <Text
              textAlign="center"
              fontSize="1.2rem"
              marginBottom="5rem"
              color="var(--amplify-colors-neutral-70)"
              maxWidth="800px"
              margin="0 auto 5rem"
              className="section-subtitle-modern"
            >
              A smarter approach to professional development
            </Text>
          </AnimatedSection>

          <div className="value-grid">
            <AnimatedSection delay={0.2}>
              <div className="value-card">
                <div className="value-card-inner">
                  <div className="value-number">01</div>
                  <Heading level={3} marginBottom="1rem">
                    Clarity
                  </Heading>
                  <Text>
                    Transform scattered experience into a coherent narrative.
                    Your skills, visualised.
                  </Text>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.3}>
              <div className="value-card">
                <div className="value-card-inner">
                  <div className="value-number">02</div>
                  <Heading level={3} marginBottom="1rem">
                    Growth
                  </Heading>
                  <Text>
                    Track evolution. Identify patterns. Make informed decisions
                    about your development.
                  </Text>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.4}>
              <div className="value-card">
                <div className="value-card-inner">
                  <div className="value-number">03</div>
                  <Heading level={3} marginBottom="1rem">
                    Impact
                  </Heading>
                  <Text>
                    Share with precision. Connect with opportunity. Stand out in
                    a crowded market.
                  </Text>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </View>

      {/* Professional Features Showcase */}
      <View className="features-showcase-section">
        <AnimatedSection>
          <Heading
            level={2}
            textAlign="center"
            marginBottom="1rem"
            className="section-title-modern"
          >
            Built for Modern Professionals
          </Heading>
          <Text
            textAlign="center"
            fontSize="1.1rem"
            marginBottom="5rem"
            color="var(--amplify-colors-neutral-70)"
            maxWidth="700px"
            margin="0 auto 5rem"
          >
            Sophisticated tools for a sophisticated career
          </Text>
        </AnimatedSection>

        <div className="features-showcase-grid">
          <AnimatedSection delay={0.2}>
            <div className="feature-showcase-card card-accent-blue">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                {t("landing.features.structuredTracking.title")}
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                {t("landing.features.structuredTracking.description")}
              </Text>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="feature-showcase-card card-accent-purple">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 20V10M12 20V4M6 20v-6" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                Visual Analytics
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                Understand your capabilities through intuitive visualisations
              </Text>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <div className="feature-showcase-card card-accent-green">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                Selective Sharing
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                Control exactly what you share, with whom, and for how long
              </Text>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.5}>
            <div className="feature-showcase-card card-accent-orange">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                Framework Alignment
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                Map your skills to industry-standard competency frameworks
              </Text>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.6}>
            <div className="feature-showcase-card card-accent-pink">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 3v18M3 9h18M3 15h18M15 3v18" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                QR Integration
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                Instant profile access at networking events and interviews
              </Text>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.7}>
            <div className="feature-showcase-card card-accent-teal">
              <div className="feature-showcase-icon" aria-hidden="true">
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <path d="M22 4L12 14.01l-3-3" />
                </svg>
              </div>
              <Heading level={4} marginBottom="0.75rem">
                Certification Proof
              </Heading>
              <Text fontSize="0.95rem" color="var(--amplify-colors-neutral-70)">
                Link verified credentials and professional certifications
              </Text>
            </div>
          </AnimatedSection>
        </div>
      </View>

      {/* AI Integration Section — repositioned before Pricing */}
      <AIIntegrationSection
        isLive={isLive}
        onNavigateToSettings={() => navigate("/settings/integrations")}
        useCases={aiUseCases}
        compatibleTools={["Kiro", "Claude Desktop", "GitHub Copilot", "Any MCP client"]}
      />

      {/* Pricing Section */}
      <View id="pricing" className="pricing-section-ultra-modern">
        <div className="pricing-ultra-bg" aria-hidden="true">
          <div className="pricing-mesh pricing-mesh-1"></div>
          <div className="pricing-mesh pricing-mesh-2"></div>
        </div>

        <div className="pricing-ultra-content">
          <AnimatedSection>
            <Heading
              level={2}
              textAlign="center"
              marginBottom="1rem"
              className="section-title-modern"
            >
              Pricing That Scales With You
            </Heading>
            <Text
              textAlign="center"
              fontSize="1.1rem"
              marginBottom="5rem"
              color="var(--amplify-colors-neutral-70)"
              maxWidth="650px"
              margin="0 auto 5rem"
            >
              Start free, upgrade when you need more. No surprises.
            </Text>
          </AnimatedSection>

          <div className="pricing-ultra-grid">
            {/* Starter Tier */}
            <AnimatedSection delay={0.2}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
                className="pricing-ultra-card"
              >
                <div className="pricing-ultra-header">
                  <div className="pricing-tier-name">Starter</div>
                  <div className="pricing-tier-price">
                    <span className="price-amount">Free</span>
                  </div>
                  <div className="pricing-tier-description">
                    Perfect for exploring and building your profile
                  </div>
                </div>

                <div className="pricing-ultra-features">
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Unlimited skill tracking</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>9,000+ prebuilt skills</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Custom skill packages</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Certification tracking</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Public profile with QR code</span>
                  </div>
                </div>

                <Button
                  className="pricing-ultra-button"
                  onClick={isLive ? () => navigate("/signup") : undefined}
                  disabled={!isLive}
                >
                  {isLive ? "Get Started" : "Coming Soon"}
                </Button>
              </motion.div>
            </AnimatedSection>

            {/* Pro Tier - Highlighted */}
            <AnimatedSection delay={0.3}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
                className="pricing-ultra-card pricing-ultra-featured"
              >
                <div className="pricing-featured-badge">Most Popular</div>

                <div className="pricing-ultra-header">
                  <div className="pricing-tier-name">Pro</div>
                  <div className="pricing-tier-price">
                    <span className="price-currency">£</span>
                    <span className="price-amount">
                      {(parseInt(import.meta.env.VITE_STRIPE_MONTHLY_PRICE) ||
                        799) / 100}
                    </span>
                    <span className="price-period">/month</span>
                  </div>
                  <div className="pricing-tier-description">
                    For professionals who want to stand out
                  </div>
                </div>

                <div className="pricing-ultra-features">
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Everything in Starter</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Competency frameworks</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Unlimited skill sharing</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Feedback & growth tools</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Priority support</span>
                  </div>
                </div>

                <Button
                  className="pricing-ultra-button pricing-ultra-button-featured"
                  onClick={isLive ? () => navigate("/signup") : undefined}
                  disabled={!isLive}
                >
                  {isLive ? "Start Free Trial" : "Coming Soon"}
                </Button>
              </motion.div>
            </AnimatedSection>

            {/* Pro Annual Tier */}
            <AnimatedSection delay={0.4}>
              <motion.div
                whileHover={{ y: -8 }}
                transition={{ duration: 0.3 }}
                className="pricing-ultra-card"
              >
                <div className="pricing-ultra-header">
                  <div className="pricing-tier-name">Pro Annual</div>
                  <div className="pricing-tier-price">
                    <span className="price-currency">£</span>
                    <span className="price-amount">
                      {(parseInt(import.meta.env.VITE_STRIPE_YEARLY_PRICE) ||
                        7900) / 100}
                    </span>
                    <span className="price-period">/year</span>
                  </div>
                  <div className="pricing-tier-description">
                    Best value for committed professionals
                  </div>
                  <div className="pricing-savings-badge">Save £16 annually</div>
                </div>

                <div className="pricing-ultra-features">
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Everything in Pro</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>2 months free</span>
                  </div>
                  <div className="feature-item">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    <span>Annual billing</span>
                  </div>
                </div>

                <Button
                  className="pricing-ultra-button"
                  onClick={isLive ? () => navigate("/signup") : undefined}
                  disabled={!isLive}
                >
                  {isLive ? "Start Free Trial" : "Coming Soon"}
                </Button>
              </motion.div>
            </AnimatedSection>
          </div>

          {/* Enterprise Section */}
          <AnimatedSection delay={0.5}>
            <div className="enterprise-ultra-card">
              <div className="enterprise-ultra-content">
                <div className="enterprise-ultra-left">
                  <div className="enterprise-ultra-badge">Enterprise</div>
                  <Heading level={3} marginBottom="1rem" color="white">
                    Built for Teams
                  </Heading>
                  <Text
                    fontSize="1.05rem"
                    marginBottom="2rem"
                    color="rgba(255, 255, 255, 0.85)"
                    lineHeight="1.6"
                  >
                    Custom frameworks • Unlimited users • Team analytics •
                    Dedicated support
                  </Text>
                  <Button
                    className="enterprise-ultra-button"
                    onClick={
                      isLive ? () => navigate("/contact?type=sales") : undefined
                    }
                    disabled={!isLive}
                  >
                    {isLive ? "Contact Sales" : "Coming Soon"}
                  </Button>
                </div>

                <div className="enterprise-ultra-right">
                  <div className="enterprise-ultra-feature">
                    <div className="enterprise-feature-icon" aria-hidden="true">∞</div>
                    <div className="enterprise-feature-text">
                      Unlimited Users
                    </div>
                  </div>
                  <div className="enterprise-ultra-feature">
                    <div className="enterprise-feature-icon" aria-hidden="true">⚡</div>
                    <div className="enterprise-feature-text">Custom Setup</div>
                  </div>
                  <div className="enterprise-ultra-feature">
                    <div className="enterprise-feature-icon" aria-hidden="true">24/7</div>
                    <div className="enterprise-feature-text">
                      Priority Support
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </View>

      {/* Final CTA */}
      <View className="final-cta-modern">
        <div className="cta-abstract-bg" aria-hidden="true">
          <div className="cta-gradient-orb orb-1"></div>
          <div className="cta-gradient-orb orb-2"></div>
          <div className="cta-gradient-orb orb-3"></div>
        </div>

        <div className="cta-content-modern">
          <AnimatedSection>
            <div className="cta-text-block">
              <Heading
                level={2}
                color="white"
                marginBottom="1.5rem"
                fontSize="2.8rem"
                className="cta-heading-modern"
              >
                Ready to elevate your professional presence?
              </Heading>
              <Text
                fontSize="1.15rem"
                color="rgba(255, 255, 255, 0.9)"
                marginBottom="3rem"
                maxWidth="650px"
                margin="0 auto 3rem"
                lineHeight="1.7"
              >
                Join forward-thinking professionals who understand that skills
                are currency in the modern workplace
              </Text>
            </div>

            <Flex
              justifyContent="center"
              gap="1.5rem"
              direction={{ base: "column", medium: "row" }}
              alignItems="center"
              className="cta-buttons-wrapper"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  variation="primary"
                  size="large"
                  onClick={isLive ? () => navigate("/signup") : undefined}
                  disabled={!isLive}
                  className="cta-primary-button"
                  aria-label={
                    isLive
                      ? "Start your journey with SkillBubble"
                      : "SkillBubble coming soon"
                  }
                >
                  {isLive ? "Start Your Journey" : "Coming Soon"}
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <Button
                  size="large"
                  onClick={() => setIsDemoModalOpen(true)}
                  className="cta-secondary-button"
                  aria-label="Explore the interactive demo"
                >
                  Explore Demo
                </Button>
              </motion.div>
            </Flex>

            <Text
              fontSize="0.9rem"
              color="rgba(255, 255, 255, 0.7)"
              marginTop="2rem"
              fontStyle="italic"
            >
              No credit card required • Free to start • Upgrade anytime
            </Text>
          </AnimatedSection>
        </div>
      </View>

      {/* Demo Modal */}
      <DemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
        isLocalhost={isLocalhost}
      />
    </View>
  );
}

export default LandingPageModern;
